import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import EntaAccordion from "common/components/EntaAccordion";
import FunctionIcon from "common/icons/FunctionIcon";
import reorderDnD from "common/utils/reorderDnD";
import { PanelSections } from "developer/edit/constants";
import useDynamicField from "developer/edit/hooks/useDynamicField";
import useIsExpanded from "developer/edit/hooks/useIsExpanded";
import { editExpressionAtProp } from "developer/edit/store/expressionEditorSlice";
import { EditorVariant, ValidationExpression } from "developer/edit/types";
import { isEmpty } from "lodash";
import React, { useCallback } from "react";
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from "react-beautiful-dnd";
import { useAppDispatch } from "store/hooks";

const ValidationExpressionsSection: React.FC = () => {
  const { isExpanded, handleChangeIsExpanded, isDefaultExpanded, hide } =
    useIsExpanded(PanelSections.ValidationExpressions);

  const dispatch = useAppDispatch();

  const {
    input: { value: validationExpressions, onChange },
  } = useDynamicField<ValidationExpression[] | undefined>(
    "fieldDef.validationExpressions"
  );

  const onDragEnd = useCallback(
    (result: DropResult) => {
      const { destination, source } = result;

      if (
        !destination ||
        (source.index === destination.index &&
          source.droppableId === destination.droppableId)
      ) {
        return;
      }

      const newOrderedOptions = reorderDnD<ValidationExpression>(
        validationExpressions!,
        source.index,
        destination.index
      );

      onChange(newOrderedOptions);
    },
    [onChange, validationExpressions]
  );

  const onAddNewExpression = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const newValidationExpression: ValidationExpression = {
        message: `Message ${(validationExpressions ?? []).length + 1}`,
        expression: "",
      };

      onChange([...(validationExpressions ?? []), newValidationExpression]);
    },
    [onChange, validationExpressions]
  );

  const onRemoveExpression =
    (index: React.Key) => (event: React.MouseEvent<HTMLButtonElement>) => {
      const newValidationExpressions = validationExpressions!.filter(
        (_, i) => i !== index
      );
      onChange(newValidationExpressions);
    };

  const onChangeMessageExpression = useCallback(
    (index: number, newMessage: string) => {
      const newValidationExpressions = structuredClone(
        validationExpressions ?? []
      );

      newValidationExpressions[index].message = newMessage;

      onChange(newValidationExpressions);
    },
    [onChange, validationExpressions]
  );

  const onEditExpression =
    (index: number, expression: string) => (event: any) => {
      const prop = `validationExpressions.${index}.expression`;
      dispatch(
        editExpressionAtProp({
          targetProp: prop,
          content: expression,
          targetEditor: EditorVariant.Schema,
        })
      );
    };

  const NoRows = useCallback(() => {
    return (
      <Box sx={{ width: 1, display: "flex", justifyContent: "center" }}>
        <Typography variant="caption" textAlign="center" width={1}>
          No Validation Expressions
        </Typography>
      </Box>
    );
  }, []);

  if (hide) return null;

  return (
    <EntaAccordion
      title="Validation Expressions"
      expanded={isExpanded}
      onChange={handleChangeIsExpanded}
      defaultExpanded={isDefaultExpanded}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            size="small"
            variant="text"
            onClick={onAddNewExpression}
            startIcon={<AddIcon fontSize="small" />}
            color="inherit"
            sx={{ textTransform: "none" }}
          >
            Add Expression
          </Button>
        </Stack>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="validation-expressions-list">
            {(droppableProvider) => (
              <List
                sx={{ maxHeight: 400, overflowY: "auto" }}
                disablePadding
                {...droppableProvider.droppableProps}
                ref={droppableProvider.innerRef}
              >
                {isEmpty(validationExpressions) ? (
                  <NoRows />
                ) : (
                  validationExpressions!.map((expression, index) => (
                    <Draggable
                      key={`${index}`}
                      draggableId={`${index}`}
                      index={index}
                    >
                      {(draggableProvider) => (
                        <ListItem
                          sx={{ py: 1 }}
                          disablePadding
                          divider
                          ref={draggableProvider.innerRef}
                          {...draggableProvider.draggableProps}
                          {...draggableProvider.dragHandleProps}
                        >
                          <Stack width={1} direction="row" spacing={2}>
                            <Box
                              sx={{
                                width: 30,
                                bgcolor: grey["100"],
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <DragIndicatorIcon />
                            </Box>
                            <Stack width={1} alignItems="center" spacing={1}>
                              <TextField
                                fullWidth
                                value={
                                  validationExpressions?.[index].message ?? ""
                                }
                                size="small"
                                label="Message"
                                name="message"
                                onChange={(event) =>
                                  onChangeMessageExpression(
                                    index,
                                    event.target.value
                                  )
                                }
                              />
                              <TextField
                                fullWidth
                                value={expression.expression}
                                size="small"
                                name="expression"
                                label="Expression"
                                InputProps={{
                                  readOnly: true,
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Tooltip title="Edit Expression">
                                        <IconButton
                                          size="small"
                                          onClick={onEditExpression(
                                            index,
                                            expression.expression
                                          )}
                                        >
                                          <FunctionIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Stack>
                            <Box
                              sx={{
                                width: 30,
                                bgcolor: grey["100"],
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <Tooltip title="Delete Expression">
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={onRemoveExpression(index)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Stack>
                        </ListItem>
                      )}
                    </Draggable>
                  ))
                )}
                {droppableProvider.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
      </Stack>
    </EntaAccordion>
  );
};

export default ValidationExpressionsSection;
